import { useColorScheme } from '@mui/material';
import { isEqual } from 'lodash';
import { useLocalStorage } from 'minimal-shared/hooks';
import { getStorage as getStorageValue } from 'minimal-shared/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { themeConfig } from '../../../theme/themeConfig';
import { SETTINGS_STORAGE_KEY } from '../settingsConfig';
import type { SettingsProviderProps, SettingsState } from '../types';
import { SettingsContext } from './settingsContext';

export function SettingsProvider({
  children,
  defaultSettings,
  storageKey = SETTINGS_STORAGE_KEY,
}: SettingsProviderProps) {
  const { setMode } = useColorScheme();
  const { state, setState, resetState, setField } = useLocalStorage<SettingsState>(storageKey, defaultSettings);

  const [openDrawer, setOpenDrawer] = useState(false);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  // The configured default wins over the OS preference: we present one appearance to everyone until
  // they pick another from the account menu.
  const setDefaultTheme = useCallback(() => {
    setMode(themeConfig.defaultMode);
  }, [setMode]);

  const canReset = !isEqual(state, defaultSettings);

  const onReset = useCallback(() => {
    setDefaultTheme();
    resetState(defaultSettings);
  }, [defaultSettings, resetState, setDefaultTheme]);

  // Version check and reset handling
  useEffect(() => {
    const storedValue = getStorageValue<SettingsState>(storageKey);

    if (storedValue) {
      try {
        if (!storedValue.version || storedValue.version !== defaultSettings.version) {
          onReset();
        }
      } catch {
        onReset();
      }
    } else {
      setDefaultTheme();
    }
  }, [defaultSettings, onReset, setDefaultTheme, storageKey]);

  const memoizedValue = useMemo(
    () => ({
      canReset,
      onReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      state,
      setState,
      setField,
    }),
    [canReset, onReset, openDrawer, onCloseDrawer, onToggleDrawer, state, setField, setState],
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
